import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, useDeviceType } from '@cian/ui-kit';

import { MainArticleAndMainNewsContainer } from '../MainArticleAndMainNews';
import { selectInitialPageNumber } from '../../selectors/settings/selectInitialPageNumber';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import {
  ApplicationContext,
  buildPostUrl,
  correctPathname,
  preparePageNumber,
  useGetListing,
  useTimeoutEffect,
  useValidTag,
} from '../../utils';
import { selectMainArticles } from '../../selectors/mainArticles';
import { Banner } from '../Banner';
import { SubscribeFormContainer } from '../SubscribeForm';
import { CardListingItem, SkeletonCardListingItem } from '../../components/ListingItem';
import { PaginationContainer } from '../Pagination';
import { PR_RANDOM } from '../../components/adfox/constants';
import { getMainArticles, resetMainArticles } from '../../actions/mainArticles';
import { MAIN_ARTICLES_REQUEST_LIMIT } from '../../constants';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { MainPageSliderContainer } from '../MainPageSlider';
import { EType } from '../../repositories/journal/entities/journal/JournalListAttributesSchema';
import { selectSlider } from '../../selectors/sliders';
import { MainPageListingHeaderContainer } from '../ListingHeader';
import { ERequestStatus } from '../../types/requestStatus';
import { selectCurrentPageMeta } from '../../selectors/pagesMeta';
import { NothingFound } from '../../components/NotFound';
import * as s from './Listings.css';

export const MainPage = () => {
  const initialPageNumber = useSelector(selectInitialPageNumber);
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const withTag = useValidTag('with-tag');
  const dispatch = useDispatch<TThunkDispatch>();
  const history = useHistory();
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const { status: listingStatus, items: articles } = useSelector(selectMainArticles);
  const appendType = useSelector(selectListingAppendType);
  const {
    custom: { mainPageSliderName = '' },
  } = useContext(ApplicationContext);
  const { items: mainPageSliderItems } = useSelector((state: IApplicationState) =>
    selectSlider(state, mainPageSliderName),
  );
  const [isSkeletonShown, setIsSkeletonShown] = useState(
    listingStatus === ERequestStatus.Loading && appendType === 'set',
  );
  const { pathname: currentPageTypePathname } = useSelector((state: IApplicationState) =>
    selectCurrentPageMeta(state, pathname),
  );
  const adfoxParams = {
    p1: 'cxytw',
    p2: 'gqui',
    puid15: 'content_part',
    pr: PR_RANDOM,
    pct: 'a',
    pfc: 'a',
    pfb: 'a',
    plp: 'a',
    pli: 'a',
    pop: 'a',
    pt: 'b',
  };

  /**
   * Запрашивает статьи на Главной/сохраняет в стор
   * Реагирует на qs-фильтры page и with-tag
   * */
  useGetListing(
    () => {
      dispatch(
        getMainArticles({
          tag: withTag,
          offset: (qsPage - 1) * MAIN_ARTICLES_REQUEST_LIMIT,
          limit: MAIN_ARTICLES_REQUEST_LIMIT,
          setType: appendType,
        }),
      ).finally();
    },
    { dependencyNames: ['page', 'with-tag', 'deviceType'] },
  );

  /**
   * В случае положительного ответа ручки с пустыми данными
   * при наличии qs-фильтров, убираем qs-фильтры,
   * чтобы попробовать получить данные снова
   */
  useEffect(() => {
    if (listingStatus === ERequestStatus.Succeed && !articles.length && (withTag || qsPage > 1)) {
      history.push(`${correctPathname(pathname)}`);
    }
  }, [articles.length, history, listingStatus, pathname, qsPage, withTag]);

  /** При выходе сбрасывает стор статей */
  useEffect(() => {
    return () => {
      dispatch(resetMainArticles());
    };
  }, [dispatch]);

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsSkeletonShown(listingStatus === ERequestStatus.Loading && appendType === 'set');
  }, [!isSkeletonShown || articles.length ? 0 : 500]);

  /**
   * При клике по плашке статьи роутит на соответствующую страницу статьи
   * При клике по шильдику рубрики в плашке статьи роутит на соответствующую рубрику
   */
  const handleClick = useCallback(
    (e, url: string) => {
      e.preventDefault();
      history.push(url);
    },
    [history],
  );

  /**
   * Шаблон для первой страницы Главной показывается в случаях:
   * - если текущая страница - первая;
   * - если текущая страница была подгружена к первой ("Показать больше");
   * Проверено! Не вызывает багов при хождении, в том числе, по истории
   */
  const isFirstPageTemplate = useCallback(
    () => qsPage === 1 || (qsPage !== 1 && initialPageNumber === 1),
    [initialPageNumber, qsPage],
  );

  return (
    <>
      <MainPageListingHeaderContainer />

      {/* Показываем только тогда, когда приложение стартовало с первой страницы */}
      {isFirstPageTemplate() && <MainArticleAndMainNewsContainer />}

      <div className={s['cards-wrapper']} data-web-ui-test-id="MainPageListing">
        {isSkeletonShown &&
          [...Array(6)].map((_, index) => (
            <Col key={index} xs={12} m={6} l={4}>
              <SkeletonCardListingItem />
            </Col>
          ))}

        {((listingStatus === ERequestStatus.Succeed && appendType === 'set') || appendType === 'append') &&
          (isFirstPageTemplate() ? articles.slice(1) : articles).map(
            (
              { id, attributes: { image, title, subtitle, datePublish, numViews, commentsCount, rubrics, slug } },
              index,
            ) => (
              <Fragment key={`list-item-${index}`}>
                {/* Баннер и слайдер только для первой страницы */}
                {isFirstPageTemplate() && index === 5 && (
                  <>
                    <Col xs={12} m={6} l={4}>
                      <Banner place={'mainpage'} constants={adfoxParams} height={'100%'} className={'wrapper-100'} />
                    </Col>

                    <div className={`${s['insert-wrapper']} ${mainPageSliderItems.length ? s['_active'] : ''}`}>
                      <MainPageSliderContainer />
                    </div>
                  </>
                )}

                {/* Форма подписки только для первой страницы */}
                {isFirstPageTemplate() && index === 11 && (
                  <div className={`${s['insert-wrapper']} ${s['_active']}`}>
                    <SubscribeFormContainer />
                  </div>
                )}

                {/* Статья */}
                <Col xs={12} m={6} l={4}>
                  <CardListingItem
                    title={title}
                    subtitle={image ? undefined : subtitle}
                    height={isPhone() ? undefined : 400}
                    image={image}
                    rubric={rubrics?.length ? rubrics[0] : undefined}
                    datePublish={datePublish}
                    numViews={numViews}
                    numComments={commentsCount}
                    url={buildPostUrl({ type: EType.Articles, slug, id })}
                    onRubricButtonClick={handleClick}
                    onClick={e => handleClick(e, buildPostUrl({ type: EType.Articles, slug, id }))}
                  />
                </Col>

                {/* Форма подписки, если приложение инициализировано НЕ на первой странице */}
                {!isFirstPageTemplate() && index === 5 && (
                  <div className={`${s['insert-wrapper']} ${s['_active']}`}>
                    <SubscribeFormContainer />
                  </div>
                )}
              </Fragment>
            ),
          )}

        {[ERequestStatus.Succeed, ERequestStatus.Failed].includes(listingStatus) && !articles.length && (
          <NothingFound pathname={currentPageTypePathname} showResetFiltersLink={qsPage > 1 || Boolean(withTag)} />
        )}
      </div>

      {listingStatus === ERequestStatus.Succeed && (
        <div className={s['controls-wrapper']}>
          <PaginationContainer />
        </div>
      )}
    </>
  );
};
